import {
    memo,
    useState,
    useEffect,
    useCallback,
    useRef,
    forwardRef,
    useImperativeHandle,
    useMemo,
    type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, isObject, noop } from 'lodash';
// Import Types
import type { CurrencyRates, DropdownOption, SpinExchangeRate } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectPartnerCurrencies } from 'store/slices/partnerSettings';
// Import UI Components
import { BusyLoader } from '@geneui/components';
// Import Components
import DataTable, { type DataTableRefActions } from 'components/common/DataTable';
import { BonusActionsModals, BonusActionsButtons, BonusOverlayContent } from 'components/BonusList';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useListFilters, type UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import Helpers
import { getBonusSupportedCurrencies } from 'services/Helpers';
// Import Services
import { BonusHttpService, TemplateHttpService, UtilsHttpService } from 'services/http';
// Import Configs
import { useDataTableColumnsConfigs, INTERNAL_TEMPLATE_LIST_CONFIG } from './configs';
// Import Constants
import { DynamicBonusTypes, l, BonusTypesLabels, KPISources, BonusTriggerTypeEnum } from 'constants/common';
// Import Styles
import 'assets/scss/templateListPage.scss';
import 'assets/scss/bonus.scss';

interface BonusRowData {
    Id: number;
    Name: string;
    Type?: string;
    [key: string]: unknown;
}

interface Props {
    increaseLoadProcessesCount?: () => void;
    decreaseLoadProcessesCount?: () => void;
    getRadioSelectedRow?: (row: { data: BonusRowData }) => void;
    getHeaderActions?: () => ReactNode;
    isHasRowRadioSelect?: boolean;
    isShowColumnChooser?: boolean;
    defaultRadioSelectedRow?: { data: BonusRowData } | null;
    setBonusDetailsAmounts: (amounts: AnyObject) => void;
    internalMessage?: number;
    setInternalMessage: (message: number) => void;
    bonusDetailsAmounts?: AnyObject;
    bonusCalculationRadioGroupValue?: number;
    setBonusCalculationRadioGroupValue: (value: number) => void;
    bonusCalculationActiveTabKey: string;
    setBonusCalculationActiveTabKey: (key: string) => void;
    setBonusCalculateAction: (action: string) => void;
    bonusCalculateAction?: string;
    bonusKind?: unknown;
    setBonusKind: (kind: unknown) => void;
    considerLimits: AnyObject;
    setConsiderLimits: (limits: AnyObject) => void;
    percentage?: unknown;
    setPercentage: (percentage: unknown) => void;
    selectedWallet?: string;
    setSelectedWallet?: (wallet: string) => void;
    selectedExpirationType?: 'Date' | 'Period' | string;
    setSelectedExpirationType?: (type: 'Date' | 'Period' | string) => void;
    expirationDateValue?: string; // ISO date string
    setExpirationDateValue?: (value: string) => void;
    expirationPeriodValue?: string; // period text or days
    setExpirationPeriodValue?: (value: string) => void;
    radioSelectColumnConfig?: Record<string, unknown>;
    isVisibleHeader?: boolean;
    listFilters: UseListFiltersReturnValue;
}

const BonusList = forwardRef<Nilable<DataTableRefActions>, Props>((props, ref) => {
    const {
        increaseLoadProcessesCount,
        decreaseLoadProcessesCount,
        getHeaderActions = noop,
        isShowColumnChooser = true,
        isHasRowRadioSelect = false,
        getRadioSelectedRow = noop,
        bonusDetailsAmounts,
        setBonusDetailsAmounts,
        internalMessage,
        setInternalMessage,
        bonusCalculationRadioGroupValue,
        setBonusCalculationRadioGroupValue,
        bonusCalculationActiveTabKey,
        setBonusCalculationActiveTabKey,
        setBonusCalculateAction,
        bonusCalculateAction,
        bonusKind,
        setBonusKind,
        considerLimits,
        setConsiderLimits,
        percentage,
        setPercentage,
        selectedWallet,
        setSelectedWallet,
        defaultRadioSelectedRow = null,
        selectedExpirationType,
        setSelectedExpirationType,
        expirationDateValue,
        setExpirationDateValue,
        expirationPeriodValue,
        setExpirationPeriodValue,
        radioSelectColumnConfig,
        isVisibleHeader,
        listFilters,
    } = props;
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();

    const [bonusSelectedRow, setBonusSelectedRow] = useState<Nullable<{ data: AnyObject }>>(null);

    const partnerCurrencies = useAppSelector(selectPartnerCurrencies);

    const dataTableRef = useRef<DataTableRefActions>(null);

    const internalMessageBaseRequestBody = useListFilters(INTERNAL_TEMPLATE_LIST_CONFIG).baseRequestBody;

    const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

    const [isBonusListLoading, setIsBonusListLoading] = useState(false);
    const [bonusListData, setBonusListData] = useState([]);
    const [bonusListDataCount, setBonusListDataCount] = useState(0);

    const [isInfoModalOpenedState, setIsInfoModalOpenedState] = useState(false);
    const [openedModalData, setOpenedModalData] = useState({});

    const { columns } = useDataTableColumnsConfigs(t);

    const {
        getBonusListRequest,
        getTemplatesRequest,
        getCurrenciesRequest,
        getBonusCalculateActionsRequest,
        getBonusObjectsByTypeRequest,
        getSpinExchangeRatesRequest,
        getBonusByIdRequest,
    } = useCancelableRequests(
        {
            getSpinExchangeRatesRequest: UtilsHttpService.getSpinExchangeRatesRequest,
            getBonusListRequest: BonusHttpService.getBonusList,
            getTemplatesRequest: TemplateHttpService.getTemplateList,
            getCurrenciesRequest: UtilsHttpService.getCurrencyRates,
            getBonusCalculateActionsRequest: UtilsHttpService.getBonusCalculateActions,
            getBonusObjectsByTypeRequest: UtilsHttpService.getBonusObjectsByType,
            getBonusByIdRequest: BonusHttpService.getBonusById,
        },
        { takeLatest: ['getBonusListRequest', 'getBonusByIdRequest'] },
    );

    // Bonus overlay content
    const [internalMessages, setInternalMessages] = useState<DropdownOption<number>[]>([]);
    const [currencies, setCurrencies] = useState<Nullable<CurrencyRates>>(null);
    const [bonusCalculateActions, setBonusCalculateActions] = useState<DropdownOption<string>[]>([]);
    const [bonusObjects, setBonusObjects] = useState<DropdownOption<string>[]>([]);
    const [bonusKPIs, setBonusKPIs] = useState<DropdownOption<string>[]>();

    const [freeSpinCurrencies, setFreeSpinCurrencies] = useState<SpinExchangeRate[]>([]);

    const getBonuses = useCallback(
        (filter = baseRequestBody) => {
            setIsBonusListLoading(true);
            increaseLoadProcessesCount?.();
            doPostRequest(getBonusListRequest.request, {
                requestBody: filter,
                successCallback: (Data) => {
                    setBonusListData((isObject(Data) && getTableRows(Data.Data)) || []);
                    setBonusListDataCount(Data.Count);
                    setIsBonusListLoading(false);
                },
                errorCallback: () => setIsBonusListLoading(false),
            }).then(() => decreaseLoadProcessesCount?.());
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseRequestBody],
    );

    const infoBonusAction = (bonus) => {
        setOpenedModalData(bonus);
        setIsInfoModalOpenedState(true);
    };

    const modalsStateSetDict = {
        infoBonus: {
            key: 'infoBonus',
            fn: setIsInfoModalOpenedState,
        },
    };

    const onCloseModalHandler = (modalStateKey) => {
        modalsStateSetDict[modalStateKey].fn(false);
    };

    const getRowActionBar = (row) => {
        const { data } = row;

        return (
            <>
                <BonusActionsButtons data={data} infoBonusAction={infoBonusAction} />
            </>
        );
    };

    const getTableRows = (bonusListData) => {
        return bonusListData.map(
            ({ Name, Type, StartDate, EndDate, Id, BonusDetails, InternalDesc, Description, TriggerType, State }) => ({
                hasHover: true,
                dragDisable: true,
                nestedData: [],
                data: {
                    Name,
                    Type,
                    StartDate,
                    EndDate,
                    Id,
                    BonusDetails,
                    InternalDesc,
                    Description,
                    TriggerType,
                    State,
                },
            }),
        );
    };

    const getTableRefreshFn = () => {
        getBonuses(baseRequestBody);
    };

    const getDataTableName = () => <span>{t(l.Bonuses)}</span>;

    // Filter update part
    useEffect(() => {
        if (isMounted.current) {
            getBonuses(baseRequestBody);
        }
    }, [baseRequestBody, getBonuses, isMounted]);

    useImperativeHandle(ref, () => ({
        resetSelectedRows() {
            dataTableRef.current?.resetSelectedRows?.();
        },
        reset() {
            dataTableRef.current?.reset?.();
        },
    }));

    const getRadioSelected = (row) => {
        if (isNil(row?.data?.Id)) {
            setBonusSelectedRow(row);
            getRadioSelectedRow?.(row);
        } else {
            increaseLoadProcessesCount?.();
            doGetRequest(getBonusByIdRequest.request, {
                queryString: { bonusId: row.data.Id },
                successCallback: (data) => {
                    const selectedRow = { data };
                    setBonusSelectedRow(selectedRow);
                    getRadioSelectedRow?.(selectedRow);
                },
            }).then(() => decreaseLoadProcessesCount?.());
        }
    };

    const init = () => {
        increaseLoadProcessesCount?.();
        doPostRequest(getTemplatesRequest.request, {
            requestBody: internalMessageBaseRequestBody,
            successCallback: (Data) => {
                setInternalMessages(
                    (isObject(Data) && Data.Data.map((item) => ({ label: item.Name, value: item.TemplateId }))) || [],
                );
            },
        }).then(() => decreaseLoadProcessesCount?.());

        increaseLoadProcessesCount?.();
        doGetRequest(getCurrenciesRequest.request, {
            successCallback: (Data) => {
                if (!isObject(Data)) return;
                setCurrencies(Data);
            },
        }).then(() => decreaseLoadProcessesCount?.());

        increaseLoadProcessesCount?.();
        doGetRequest(getBonusCalculateActionsRequest.request, {
            successCallback: (Data) => {
                if (!isObject(Data)) return;
                setBonusCalculateActions(Data.map((item) => ({ label: item.Name, value: item.Id })));
            },
        }).then(() => decreaseLoadProcessesCount?.());

        increaseLoadProcessesCount?.();
        doGetRequest(getBonusObjectsByTypeRequest.request, {
            queryString: { type: KPISources.Object },
            successCallback: (Data) => {
                if (!isObject(Data)) return;
                setBonusObjects(Data.map((item) => ({ label: item.Name, value: item.Id })));
            },
        }).then(() => decreaseLoadProcessesCount?.());

        increaseLoadProcessesCount?.();
        doGetRequest(getBonusObjectsByTypeRequest.request, {
            queryString: { type: KPISources.KPI },
            successCallback: (Data) => {
                if (!isObject(Data)) return;
                setBonusKPIs(Data.map((item) => ({ label: item.Name, value: item.Id })));
            },
        }).then(() => decreaseLoadProcessesCount?.());

        increaseLoadProcessesCount?.();
        doGetRequest(getSpinExchangeRatesRequest.request, {
            queryString: {
                bonusType: BonusTypesLabels.FreeSpin,
            },
            successCallback: (data) => {
                setFreeSpinCurrencies(data);
            },
        }).then(() => decreaseLoadProcessesCount?.());
    };

    const setIsVisibleBonusInfoModal = (isVisible) => {
        setIsInfoModalOpenedState(isVisible);
        if (!isNil(bonusSelectedRow?.data) && isVisible === true) {
            setOpenedModalData(bonusSelectedRow!.data);
        }
    };

    const supportedWallets = useMemo(() => {
        return partnerCurrencies.filter((el) => el.IsWalletAutocreatable);
    }, [partnerCurrencies]);

    const isTriggerRequired = bonusSelectedRow?.data?.TriggerType !== BonusTriggerTypeEnum.Manual;
    const isBoostBonus = bonusSelectedRow?.data?.Type === BonusTypesLabels.BoostBonus;
    const isNFTBonus = bonusSelectedRow?.data?.Type === BonusTypesLabels.NFTBonus;
    const supportedCurrencies = useMemo(() => getBonusSupportedCurrencies(bonusSelectedRow?.data), [bonusSelectedRow]);

    const isVisibleRadioSelection = !isTriggerRequired;

    const isVisibleCalculationSection =
        !isNFTBonus &&
        (!isTriggerRequired ||
            (!isEmpty(supportedWallets) && (isBoostBonus || supportedCurrencies.includes(selectedWallet))));

    const isVisibleAlert =
        !isNFTBonus && ((isTriggerRequired && isEmpty(supportedWallets)) || !isVisibleRadioSelection);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    const isLoading =
        isNil(bonusSelectedRow) ||
        isNil(internalMessages) ||
        isNil(currencies) ||
        isNil(bonusCalculateActions) ||
        isNil(bonusObjects) ||
        isNil(bonusKPIs);

    const bonusKinds = useMemo(() => {
        return {
            [DynamicBonusTypes.Object]: bonusObjects,
            [DynamicBonusTypes.KPI]: bonusKPIs,
        };
    }, [bonusKPIs, bonusObjects]);

    return (
        <>
            <DataTable
                classNames="bonus-list-table"
                rowKey="Id"
                columnKey="dataKey"
                name={getDataTableName()}
                isColumnsSortable={true}
                withPagination={true}
                data={bonusListData}
                dataCount={bonusListDataCount}
                rowActionBar={getRowActionBar}
                columns={columns}
                isLoading={isBonusListLoading}
                isShowRefreshButton={true}
                isShowColumnChooser={isShowColumnChooser}
                setPagingPageSize={changePageSize}
                setPagingPageNumber={changePageNumber}
                setSorting={changeSorting}
                onRefreshClick={getTableRefreshFn}
                headerActions={getHeaderActions?.()}
                isHasRowRadioSelect={isHasRowRadioSelect}
                getRadioSelectedRow={getRadioSelected}
                withOverlay={true}
                defaultRadioSelectedRow={defaultRadioSelectedRow}
                ref={dataTableRef}
                radioSelectColumnConfig={radioSelectColumnConfig}
                isVisibleHeader={isVisibleHeader}
                overlayContent={
                    <BusyLoader isBusy={isLoading}>
                        {!isLoading && (
                            <BonusOverlayContent
                                name={bonusSelectedRow.data.Name}
                                description={bonusSelectedRow.data.Description}
                                type={bonusSelectedRow.data.Type}
                                bonusDetails={bonusSelectedRow.data.BonusDetails}
                                bonusDetailsAmounts={bonusDetailsAmounts ?? {}}
                                setBonusDetailsAmounts={setBonusDetailsAmounts}
                                internalMessages={internalMessages}
                                internalMessage={internalMessage}
                                setInternalMessage={setInternalMessage}
                                bonusCalculationRadioGroupValue={bonusCalculationRadioGroupValue}
                                setBonusCalculationRadioGroupValue={setBonusCalculationRadioGroupValue}
                                bonusCalculationActiveTabKey={bonusCalculationActiveTabKey}
                                setBonusCalculationActiveTabKey={setBonusCalculationActiveTabKey}
                                bonusCalculateActions={bonusCalculateActions}
                                bonusCalculateAction={bonusCalculateAction}
                                setBonusCalculateAction={setBonusCalculateAction}
                                considerLimits={considerLimits}
                                setConsiderLimits={setConsiderLimits}
                                percentage={percentage ?? {}}
                                setPercentage={setPercentage}
                                bonusKind={bonusKind ?? {}}
                                bonusKinds={bonusKinds}
                                setBonusKind={setBonusKind}
                                isVisibleRadioSelection={isVisibleRadioSelection}
                                isVisibleCalculationSection={isVisibleCalculationSection}
                                isVisibleAlert={isVisibleAlert}
                                setIsVisibleBonusInfoModal={setIsVisibleBonusInfoModal}
                                freeSpinCurrencies={freeSpinCurrencies}
                                supportedCurrencies={supportedCurrencies}
                                selectedWallet={selectedWallet}
                                setSelectedWallet={setSelectedWallet}
                                selectedExpirationType={selectedExpirationType}
                                setSelectedExpirationType={setSelectedExpirationType}
                                expirationDateValue={expirationDateValue}
                                setExpirationDateValue={setExpirationDateValue}
                                expirationPeriodValue={expirationPeriodValue}
                                setExpirationPeriodValue={setExpirationPeriodValue}
                                supportedWallets={supportedWallets}
                            />
                        )}
                    </BusyLoader>
                }
            />

            <BonusActionsModals
                isInfoModalOpenedState={isInfoModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={openedModalData}
            />
        </>
    );
});

BonusList.displayName = 'BonusList';

export default memo(BonusList);
